import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

interface LoginScreenProps {
  onNavigateToCodeEntry?: () => void;
}

export function LoginScreen({ onNavigateToCodeEntry }: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { sendSignInLink } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setMessage(null);

    const result = await sendSignInLink(email);

    if (result.success) {
      // Navigate immediately to code entry screen
      if (onNavigateToCodeEntry) {
        onNavigateToCodeEntry();
      }
    } else {
      setMessage({
        type: "error",
        text: "Failed to send sign-in link. Please try again.",
      });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="max-w-md w-full px-8">
        {/* Main Content */}
        <div className="flex flex-col items-center">
          {/* Logo */}
          <img
            src="/Rebuild_Logo_White.png"
            alt="Rebuild Logo"
            className="h-16 mb-6"
          />
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2"> Tools</h1>
          <p className="text-xl text-gray-400 mb-8">Sign in to continue</p>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="w-full">
            <div className="mb-4">
              <label className="block text-gray-400 mb-2 text-sm">Email</label>
              <input
                type="email"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Message */}
            {message && (
              <div className="mb-4">
                <p className={`text-center text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}>
                  {message.text}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors mb-4"
              disabled={isSubmitting || !email.trim()}
            >
              {isSubmitting ? "Sending..." : "Send Code"}
            </button>
          </form>
        </div>

        {/* Legal Links - Bottom */}
        <div className="mt-8 text-center">
          <div className="flex justify-center items-center space-x-4 text-sm">
            <a
              href="https://www.rebuild.work/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-400"
            >
              Terms
            </a>
            <span className="text-gray-600">•</span>
            <a
              href="https://www.rebuild.work/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-400"
            >
              Privacy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
